import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { createContext, use, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

type ToastTone = 'success' | 'danger' | 'info';

export interface ToastMessage {
  readonly tone?: ToastTone;
  readonly title: string;
  readonly description?: string;
}

interface ShownToast extends ToastMessage {
  readonly id: number;
}

type ShowToast = (message: ToastMessage) => void;

const ToastContext = createContext<ShowToast | null>(null);

const VISIBLE_MS = 4_500;

const icons: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 aria-hidden="true" className="size-5 text-emerald-600" />,
  danger: <XCircle aria-hidden="true" className="size-5 text-red-600" />,
  info: <Info aria-hidden="true" className="size-5 text-sky-600" />,
};

function ToastCard({
  toast,
  onDismiss,
}: {
  readonly toast: ShownToast;
  readonly onDismiss: (id: number) => void;
}) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, VISIBLE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, onDismiss]);

  return (
    <li className="pointer-events-auto flex w-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-lg shadow-slate-900/10 animate-toast-in sm:w-96">
      {icons[toast.tone ?? 'success']}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{toast.title}</p>
        {toast.description ? (
          <p className="mt-0.5 text-sm text-slate-600">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Melding sluiten"
        onClick={() => {
          onDismiss(toast.id);
        }}
        className="-m-1 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 pointer-coarse:-m-2 pointer-coarse:p-2"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </li>
  );
}

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<ShownToast[]>([]);
  const nextId = useRef(0);

  const show = useCallback<ShowToast>((message) => {
    nextId.current += 1;
    const id = nextId.current;
    setToasts((current) => [...current.slice(-3), { ...message, id }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext value={show}>
      {children}
      <section
        aria-label="Meldingen"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[60] sm:inset-x-auto sm:right-6 sm:bottom-6"
      >
        <ol className="flex flex-col items-end gap-2">
          {toasts.map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </ol>
      </section>
    </ToastContext>
  );
}

export function useToast(): ShowToast {
  const show = use(ToastContext);
  if (!show) {
    throw new Error('useToast needs a ToastProvider');
  }
  return show;
}
